import React from 'react';

import type { DistributionValue } from '@ha/core/SearchFilters/priceDistribution/types';

import { DEFAULT_CURRENCY } from 'ha/constants/Currencies';
import { DEFAULT_SEARCH_FLEXIBILITY } from 'ha/constants/FlexibleSearchDates';

import {
  AdvertiserRatingFilterValue,
  AmenitiesFilterValue,
  BillsFilterValue,
  ContractTypeFilterValue,
  FacilitiesFilterValue,
  FurnitureFilterValue,
  GenderFilterValue,
  DepositPolicyFilterValue,
  PriceFilterValue,
  PropertySizeFilterValue,
  RadiusFilterValue,
  RadiusValue,
  RecentlyAddedFilterValue,
  RegistrationFilterValue,
  RoomsFilterValue,
  RulesFilterValue,
  SortingValue,
  SuitableForFilterValue,
  TypeFilterValue,
  CancellationPolicyFilterValue,
} from 'ha/types/SearchFilters';

import { Filters } from './types';

export interface LocalState extends Filters {
  place?: string;
}

export interface SearchFiltersContextValue {
  localState: LocalState;
  priceParams: {
    priceMin: number;
    priceMax: number;
    priceDistribution: DistributionValue[];
  };
  onChangeTypes: (_: TypeFilterValue) => void;
  onChangeRooms: (_: RoomsFilterValue) => void;
  onChangePrice: (_: PriceFilterValue) => void;
  onChangeCurrency: (_: string) => void;
  onChangeAdvertiserRatings: (_: AdvertiserRatingFilterValue) => void;
  onChangeFurniture: (_: FurnitureFilterValue) => void;
  onChangeBills: (_: BillsFilterValue) => void;
  onChangeNoDeposit: (_: DepositPolicyFilterValue) => void;
  onChangeSuitableFor: (_: SuitableForFilterValue) => void;
  onChangeGender: (_: GenderFilterValue) => void;
  onChangeRules: (_: RulesFilterValue) => void;
  onChangeRegistration: (_: RegistrationFilterValue) => void;
  onChangeRadius: (_: RadiusFilterValue) => void;
  onChangeRecentlyAdded: (_: RecentlyAddedFilterValue) => void;
  onChangeContractType: (_: ContractTypeFilterValue) => void;
  onChangePropertySize: (_: PropertySizeFilterValue) => void;
  onChangeFacilities: (_: FacilitiesFilterValue) => void;
  onChangeAmenities: (_: AmenitiesFilterValue) => void;
  onChangeSortBy: (_: SortingValue) => void;
  clearFilters: (filtersToKeep?: Array<keyof LocalState>) => void;
  restoreFilters: (filters: Partial<LocalState>) => void;
  isFilterEmpty: (filterName: keyof LocalState) => boolean;
  applyFilters: () => void;
  onChangeNeighborhood: (_: string[]) => void;
  onChangeCancellationPolicy: (_: CancellationPolicyFilterValue) => void;
}

export const EmptyState: LocalState = {
  startDate: undefined,
  endDate: undefined,
  flexDays: DEFAULT_SEARCH_FLEXIBILITY.Search,
  categories: undefined,
  advRating: undefined,
  furniture: undefined,
  bills: undefined,
  depositPolicy: undefined,
  bedroomCount: undefined,
  rules: undefined,
  priceMin: undefined,
  priceMax: undefined,
  currency: DEFAULT_CURRENCY,
  registration: undefined,
  recentlyAdded: undefined,
  gender: undefined,
  suitableFor: undefined,
  contractType: undefined,
  radius: RadiusValue.DEFAULT,
  place: '',
  sizeMin: undefined,
  sizeMax: undefined,
  facilities: undefined,
  amenities: undefined,
  neighborhoods: undefined,
  cancellationPolicy: undefined,
};

export const SearchFiltersContext =
  React.createContext<SearchFiltersContextValue>({
    localState: EmptyState,
    priceParams: {
      priceMin: 0,
      priceMax: 0,
      priceDistribution: [],
    },
    onChangeTypes: (_: TypeFilterValue) => {},
    onChangeRooms: (_: RoomsFilterValue) => {},
    onChangePrice: (_: PriceFilterValue) => {},
    onChangeCurrency: (_: string) => {},
    onChangeAdvertiserRatings: (_: AdvertiserRatingFilterValue) => {},
    onChangeFurniture: (_: FurnitureFilterValue) => {},
    onChangeBills: (_: BillsFilterValue) => {},
    onChangeNoDeposit: (_: DepositPolicyFilterValue) => {},
    onChangeSuitableFor: (_: SuitableForFilterValue) => {},
    onChangeGender: (_: GenderFilterValue) => {},
    onChangeRules: (_: RulesFilterValue) => {},
    onChangeRegistration: (_: RegistrationFilterValue) => {},
    onChangeRadius: (_: RadiusFilterValue) => {},
    onChangeRecentlyAdded: (_: RecentlyAddedFilterValue) => {},
    onChangeContractType: (_: ContractTypeFilterValue) => {},
    onChangePropertySize: (_: PropertySizeFilterValue) => {},
    onChangeFacilities: (_: FacilitiesFilterValue) => {},
    onChangeAmenities: (_: AmenitiesFilterValue) => {},
    onChangeSortBy: (_: SortingValue) => {},
    onChangeNeighborhood: (_: string[]) => {},
    clearFilters: () => {},
    restoreFilters: () => {},
    isFilterEmpty: () => true,
    applyFilters: () => {},
    onChangeCancellationPolicy: (_: CancellationPolicyFilterValue) => {},
  });

SearchFiltersContext.displayName = 'SearchFiltersContext';
